import { getTotalItemPricing } from 'common/modules/atoms/pricing';
import {
	getTaxAmountsFromPrice,
	getTaxPercentFromTaxRate,
	getUniqueTaxRatesAndLabels,
	hasSameRateAndLabel,
} from 'common/modules/atoms/taxes';
import {
	AccountingGroup,
	AccountingRow,
	ItemObject,
	PaymentAccountingRow,
	PaymentApi,
	ReceiptAccountingRow,
} from 'common/types';
import { TFunction } from 'i18next';
import { values, groupBy, chunk } from 'lodash';
import moment from 'moment-timezone';
import { PaymentWithTransactions } from 'views/Rental/PaymentOverview/PaymentsList/utils';
import { ReportData } from './types';
import { api } from 'common/frontend/api';
import { LocaleHelpers } from 'common/locales/helpers';

export const NO_GROUP_ID = '__NO_GROUP__';
export const othersAccountingGroup = (t: TFunction): AccountingGroup => ({
	id: NO_GROUP_ID,
	name: t('reports.accounting.unGrouped', 'Ungrouped'),
});

export const allTableSections = [
	'date_breakdown',
	'method_breakdown',
	'item_breakdown',
	'tax_breakdown',
	'custom_group_breakdown',
] as const;

export const allAccountingTables = ['bookings', 'sales', 'refunds', 'fees'] as const;

export const fetchReportData = async (
	startDate: moment.Moment | null,
	endDate: moment.Moment | null,
	shopId: string,
): Promise<ReportData> => {
	const dbApi = api();
	if (!startDate || !endDate) {
		return {
			SALES: [],
			BOOKING: [],
			REFUND: [],
		};
	}
	const start = moment(startDate).startOf('day').toISOString();
	const end = moment(endDate).endOf('day').toISOString();
	const salesRef = dbApi.payments.get
		.where('shopId', '==', shopId)
		.where('capture', '==', true)
		.where('completed', '==', true)
		.whereIn('paymentAction', 'in', ['PAYMENT', 'CAPTURE'])
		.orderBy('usageDate', 'asc')
		.startAt(start)
		.endAt(end);
	const bookingsRef = dbApi.payments.get
		.where('shopId', '==', shopId)
		.where('capture', '==', true)
		.where('completed', '==', true)
		.whereIn('paymentAction', 'in', ['PAYMENT', 'CAPTURE'])
		.orderBy('timestamp', 'asc')
		.startAt(start)
		.endAt(end);
	const refundsRef = dbApi.payments.get
		.where('shopId', '==', shopId)
		.where('capture', '==', true)
		.where('completed', '==', true)
		.where('paymentAction', '==', 'REFUND')
		.orderBy('timestamp', 'asc')
		.startAt(start)
		.endAt(end);
	const payments = await Promise.all([
		salesRef.get().then((res) => res.filter(removeManualOrPosPayments)),
		bookingsRef.get().then((res) => res.filter(removeManualOrPosPayments)),
		refundsRef.get().then((res) => res.filter(removeManualOrPosPayments)),
	]);
	const allTransactionIds = payments
		.flat()
		.flatMap((payment) => payment.transactionIds)
		.flat();
	const allTransactions = await getAllTransactions({
		transactionIds: allTransactionIds,
		shopId,
		prefetchDates: { start, end },
	});
	const transactionsGroupedByPaymentId = groupBy(allTransactions, 'paymentId');
	const [salesPayments, bookingsPayments, refundsPayments] = payments;
	return {
		SALES: salesPayments.map((payment) => ({
			payment,
			transactions: transactionsGroupedByPaymentId[payment.id] ?? [],
		})),
		BOOKING: bookingsPayments.map((payment) => ({
			payment,
			transactions: transactionsGroupedByPaymentId[payment.id] ?? [],
		})),
		REFUND: refundsPayments.map((payment) => ({
			payment,
			transactions: transactionsGroupedByPaymentId[payment.id] ?? [],
		})),
	};
};

const getAllTransactions = async (args: {
	transactionIds: string[];
	shopId: string;
	prefetchDates: { start: string; end: string };
}) => {
	const {
		transactionIds,
		prefetchDates: { start, end },
		shopId,
	} = args;
	// Most of the transactions are from the same date range as payments, so we reduce the number of queries by trying to fetch many transactions from the same range using a single query
	const prefetchedTransactionsPool = await api()
		.transactions.get.where('shopId', '==', shopId)
		.orderBy('timestamp', 'asc')
		.startAt(start)
		.endAt(end)
		.get();
	const prefetchedTransactions = prefetchedTransactionsPool.filter((transaction) =>
		transactionIds.includes(transaction.id),
	);
	const prefetchedTransactionIds = prefetchedTransactions.map((t) => t.id);
	const nonFetchedTransactionIds = transactionIds.filter(
		(id) => !prefetchedTransactionIds.includes(id),
	);
	// Where IN query only supports up to 10 values at a time
	const idChunksOfTen = chunk(nonFetchedTransactionIds, 10);
	const transactions = await Promise.all(
		idChunksOfTen.map((idChunk) =>
			api().transactions.get.where('shopId', '==', shopId).whereIn('id', 'in', idChunk).get(),
		),
	).then((transactionsList) => transactionsList.flat());
	return [...prefetchedTransactions, ...transactions];
};

const removeManualOrPosPayments = (data: PaymentApi) => {
	return (
		(data.method && data.method !== 'MANUAL' && data.method !== 'POS') ||
		((data.methods || []).length &&
			data.methods.every((method) => method !== 'MANUAL' && method !== 'POS'))
	);
};

export const getTaxesFromItemObjects = (
	items: ItemObject[],
	paymentId: string,
): AccountingRow[] => {
	const taxLines = items.flatMap((i) => i.pricing.taxLines);
	const uniqueTaxRatesAndLabels = getUniqueTaxRatesAndLabels(taxLines);
	return uniqueTaxRatesAndLabels.flatMap(([rate, label]) => {
		const validItems = items.filter((item) =>
			item.pricing.taxLines.some((line) => hasSameRateAndLabel(line, { rate, label })),
		);
		const taxPercent = `${getTaxPercentFromTaxRate(rate).toString()}`;
		const name = label ? `${label} (${taxPercent}%)` : `${taxPercent}%`;
		return getAccountingRowFromItemObjects(validItems, paymentId, name);
	});
};

const getAccountingRowFromItemObjects = (
	items: ItemObject[],
	paymentId: string,
	label: string,
): AccountingRow[] => {
	const taxLines = items.flatMap((i) => i.pricing.taxLines);
	const uniqueTaxRatesAndLabels = getUniqueTaxRatesAndLabels(taxLines);
	return uniqueTaxRatesAndLabels.map(([rate, taxLabel]) => {
		const validItems = items.filter((item) =>
			item.pricing.taxLines.some((line) => hasSameRateAndLabel(line, { rate, label: taxLabel })),
		);
		const pricing = getTotalItemPricing(validItems.map((i) => i.pricing));
		const total = pricing.total;
		const taxAmount = validItems
			.flatMap((item) => item.pricing.taxLines)
			.filter((line) => hasSameRateAndLabel(line, { rate, label: taxLabel }))
			.reduce((tot, curr) => tot + curr.price, 0);
		const netAmount = total - taxAmount;
		return {
			id: `${rate}_${taxLabel}`,
			name: label,
			totalAmount: total,
			netAmount,
			taxAmount,
			taxPercent: getTaxPercentFromTaxRate(rate),
			paymentIds: [paymentId],
			units: validItems.length,
		};
	});
};

export const getCustomAccountingGroupsFromItemObjects = (
	items: ItemObject[],
	paymentId: string,
	accountingGroups: AccountingGroup[],
	productsIdAccountingGroupIdMap: { [productId: string]: string[] },
	t: TFunction,
): AccountingRow[] => {
	const allAccountingGroups = [...accountingGroups, othersAccountingGroup(t)];
	return allAccountingGroups.flatMap((group) => {
		const groupId = group.id;
		const groupName = group.name;
		const itemsByGroupId = items.filter((item) => {
			const itemStockProductId = item.stockProductId;
			const accountingGroupIds = itemStockProductId
				? productsIdAccountingGroupIdMap[itemStockProductId] ?? [NO_GROUP_ID]
				: [NO_GROUP_ID];
			return accountingGroupIds.includes(groupId);
		});
		return getAccountingRowFromItemObjects(itemsByGroupId, paymentId, groupName);
	});
};

export const getItemNamesFromItemObjects = (
	items: ItemObject[],
	paymentId: string,
): AccountingRow[] => {
	const allItemNames = [...new Set(items.map((item) => item.name))];
	return allItemNames.flatMap((itemName) => {
		const itemsByName = items.filter((item) => item.name === itemName);
		return getAccountingRowFromItemObjects(itemsByName, paymentId, itemName);
	});
};

export const getFeeRowsFromFeeItemObject = (
	item: ItemObject,
	paymentId: string,
	{ negative }: { negative: boolean },
): AccountingRow[] => {
	const subtotal = item.pricing.subtotal * (negative ? -1 : 1);
	return item.pricing.taxLines.map((line) => {
		const taxAmounts = getTaxAmountsFromPrice(subtotal, {
			taxExcluded: item.pricing.taxExcluded,
			taxRate: line.rate,
		});
		return {
			id: `fee_${line.rate}`,
			name: `Processing fee`,
			totalAmount: taxAmounts.total,
			netAmount: taxAmounts.netAmount,
			taxAmount: taxAmounts.taxAmount,
			taxPercent: getTaxPercentFromTaxRate(line.rate),
			paymentIds: [paymentId],
		};
	});
};

export const getAccountingRowsDividedByTaxRate = (
	rows: AccountingRow[],
	opts?: { nullifyTax: boolean },
): AccountingRow[] => {
	const partitionedByTaxPercent = values(groupBy(rows, 'taxPercent'));
	return partitionedByTaxPercent.map((rowsByTaxPercent) => {
		const totalAmount = rowsByTaxPercent
			.map((row) => row.totalAmount || 0)
			.reduce((a, b) => a + b, 0);
		const taxAmount = !!opts?.nullifyTax
			? null
			: Math.round(rowsByTaxPercent.map((row) => row.taxAmount || 0).reduce((a, b) => a + b, 0));
		const netAmount = taxAmount != null ? totalAmount - taxAmount : null;
		const taxPercent = rowsByTaxPercent[0] ? rowsByTaxPercent[0].taxPercent : null;
		const name = rowsByTaxPercent[0] ? rowsByTaxPercent[0].name : '';
		const paymentIds = [...new Set(rowsByTaxPercent.flatMap((row) => row.paymentIds))];
		const units = rowsByTaxPercent.map((row) => row.units || 1).reduce((a, b) => a + b, 0);
		return {
			id: `${name}_${taxPercent}`,
			name,
			totalAmount,
			netAmount,
			taxAmount,
			paymentIds,
			taxPercent,
			...(units && { units }),
		};
	});
};

export const getFeesFromPaymentAccountingRows = (rows: PaymentAccountingRow[]): AccountingRow[] => {
	const feeRows = rows.map((row) => row.feeRows).flat();
	const allFees = [...new Set(feeRows.map((feeRow) => feeRow.name))];
	return allFees
		.map((fee) => {
			const feeRowsByName = feeRows.filter((feeRow) => feeRow.name === fee);
			return getAccountingRowsDividedByTaxRate(feeRowsByName);
		})
		.flat()
		.sort(sortAccountingRowsByName);
};

export const getTaxesFromPaymentAccountingRows = (
	rows: PaymentAccountingRow[],
): AccountingRow[] => {
	const taxRows = rows.map((row) => row.taxRows).flat();
	const allTaxPercents = [...new Set(taxRows.map((taxRow) => taxRow.taxPercent))];
	return allTaxPercents
		.map((taxPercent) => {
			const vatRowsWithPercentage = taxRows.filter((row) => row.taxPercent === taxPercent);
			const totalAmount = vatRowsWithPercentage
				.map((row) => row.totalAmount || 0)
				.reduce((a, b) => a + b, 0);
			const netAmount = Math.round(
				vatRowsWithPercentage.map((row) => row.netAmount || 0).reduce((a, b) => a + b, 0),
			);
			const taxAmount = netAmount !== 0 ? totalAmount - netAmount : 0;
			const paymentIds = [...new Set(vatRowsWithPercentage.map((row) => row.paymentIds).flat())];
			const units = vatRowsWithPercentage.map((row) => row.units || 1).reduce((a, b) => a + b, 0);
			const name = taxPercent != null ? taxPercent.toString() : 'Undefined';
			return {
				id: name,
				name,
				totalAmount,
				netAmount,
				taxAmount,
				taxPercent,
				paymentIds,
				units,
			};
		})
		.sort(sortAccountingRowsByName);
};

export const getItemNamesFromPaymentAccountingRows = (
	rows: PaymentAccountingRow[],
): AccountingRow[] => {
	const itemRows = rows.map((row) => row.itemNameRows || []).flat();
	const allItemNames = [...new Set(itemRows.map((itemNameRow) => itemNameRow.name))];
	return allItemNames
		.map((itemName) => {
			const itemRowsWithName = itemRows.filter((row) => row.name === itemName);
			return getAccountingRowsDividedByTaxRate(itemRowsWithName);
		})
		.flat()
		.sort(sortAccountingRowsByUnits);
};

export const getCustomAccountingGroupsFromPaymentAccountingRows = (
	rows: PaymentAccountingRow[],
): AccountingRow[] => {
	const groupRows = rows.map((row) => row.customAccountingGroupRows || []).flat();
	const allGroupNames = [...new Set(groupRows.map((groupRow) => groupRow.name))];
	return allGroupNames
		.map((groupName) => {
			const groupRowsWithName = groupRows.filter((row) => row.name === groupName);
			return getAccountingRowsDividedByTaxRate(groupRowsWithName);
		})
		.flat()
		.sort(sortAccountingRowsByName);
};

export const getMethodsFromPaymentAccountingRows = (
	rows: PaymentAccountingRow[],
	t: TFunction,
): AccountingRow[] => {
	const methodRows = rows.flatMap((row) => row.methodRows);
	const allMethodNames = [...new Set(methodRows.map((methodRow) => methodRow.name))];
	return allMethodNames
		.map((name) => {
			const methodRowsWithName = methodRows.filter((row) => row.name === name);
			return getAccountingRowsDividedByTaxRate(methodRowsWithName, { nullifyTax: true });
		})
		.flat()
		.map((row) => ({ ...row, name: LocaleHelpers.admin(t).paymentMethods[row.name] ?? row.name }))
		.sort(sortAccountingRowsByName);
};

export const getDatesFromPaymentAccountingRows = (
	rows: PaymentAccountingRow[],
): AccountingRow[] => {
	const allDates = [...new Set(rows.map((row) => row.date))];
	return allDates
		.map((date) => {
			const dateRows = rows.filter((row) => row.date === date);
			const totalAmount = dateRows.map((row) => row.totalAmount).reduce((a, b) => a + b, 0);
			const netAmount = Math.round(dateRows.map((row) => row.netAmount).reduce((a, b) => a + b, 0));
			const taxAmount = netAmount !== 0 ? totalAmount - netAmount : 0;
			const paymentIds = dateRows.map((row) => row.paymentId);
			const units = dateRows.length;
			return {
				id: date,
				name: date,
				totalAmount,
				netAmount,
				taxAmount,
				paymentIds,
				units,
				taxPercent: null,
			};
		})
		.sort(sortAccountingRowsByName);
};

export const getReceiptRows = (
	field: 'method' | 'fee' | 'itemName',
	name: string,
	rows: PaymentAccountingRow[],
): ReceiptAccountingRow[] => {
	return rows.map((row) => {
		const { paymentId, orderId, receiptNumber, receiptId } = row;
		const accountingRowsToUse =
			field === 'method'
				? row.methodRows
				: field === 'fee'
				? row.feeRows
				: field === 'itemName'
				? row.itemNameRows || []
				: [];
		const validRows = accountingRowsToUse.flat().filter((accRow) => accRow.name === name);
		const totalAmount = validRows.map((row) => row.totalAmount || 0).reduce((a, b) => a + b, 0);
		const netAmount = Math.round(
			validRows.map((row) => row.netAmount || 0).reduce((a, b) => a + b, 0),
		);
		const taxAmount = netAmount !== 0 ? totalAmount - netAmount : 0;
		const receiptRow: ReceiptAccountingRow = {
			receiptNumber,
			receiptId,
			paymentId,
			netAmount,
			taxAmount,
			totalAmount,
			orderId,
		};
		return receiptRow;
	});
};

const isItemIncludedInAccountingGroupIds = (
	item: ItemObject,
	stockProductIdAccountingGroupMap: { [productId: string]: string[] },
	selectedAccountingGroupIds: string[],
) => {
	const productAccountingGroupIds = !item.stockProductId
		? [NO_GROUP_ID]
		: stockProductIdAccountingGroupMap[item.stockProductId] ?? [NO_GROUP_ID];
	return productAccountingGroupIds.some((id) => selectedAccountingGroupIds.includes(id));
};

export const paymentDataToPaymentAccountingRow = (
	data: PaymentWithTransactions,
	paymentType: 'SALES' | 'REFUND' | 'BOOKING',
	customAccountingGroups: AccountingGroup[],
	stockProductIdAccountingGroupMap: { [productId: string]: string[] },
	accountingGroupIdsToShow: string[],
	t: TFunction,
): PaymentAccountingRow | null => {
	const shopHasCustomAccountingGroups = !!customAccountingGroups.length;
	const payment = data.payment;
	const transactions = data.transactions;
	const filterPaymentItemsByAccountingGroup =
		shopHasCustomAccountingGroups && accountingGroupIdsToShow.length;
	const paymentItems = !filterPaymentItemsByAccountingGroup
		? payment.items
		: payment.items.filter((item) =>
				isItemIncludedInAccountingGroupIds(
					item,
					stockProductIdAccountingGroupMap,
					accountingGroupIdsToShow,
				),
		  );
	if (!paymentItems.length) return null;
	const feeRows: AccountingRow[] = [];
	let methodRows: AccountingRow[] = [];
	for (const transaction of transactions) {
		if (transaction.fee) {
			const feeRow = getFeeRowsFromFeeItemObject(transaction.fee, payment.id, {
				negative: transaction.paymentType === 'REFUND',
			});
			feeRows.push(...feeRow);
		}
		const { method, amount } = transaction;
		if (methodRows.find((row) => row.name === method)) {
			methodRows = methodRows.map((row) => {
				if (row.name === method) {
					return {
						...row,
						totalAmount: row.totalAmount + amount.value,
					};
				}
				return row;
			});
		} else {
			const methodRow: AccountingRow = {
				id: method,
				name: method,
				totalAmount: amount.value,
				netAmount: null,
				taxAmount: null,
				paymentIds: [transaction.paymentId],
				taxPercent: null,
			};
			methodRows = [...methodRows, methodRow];
		}
	}
	const taxRows = getTaxesFromItemObjects(paymentItems, payment.id);
	const itemNameRows = getItemNamesFromItemObjects(paymentItems, payment.id);
	const customAccountingGroupRows = getCustomAccountingGroupsFromItemObjects(
		paymentItems,
		payment.id,
		customAccountingGroups,
		stockProductIdAccountingGroupMap,
		t,
	);
	const paymentDate =
		paymentType === 'SALES' ? moment(payment.usageDate) : moment(payment.timestamp);
	const paymentRow: PaymentAccountingRow = {
		date: moment(paymentDate).format('YYYY-MM-DD'),
		type: paymentType,
		receiptNumber: payment.receiptNumber || null,
		reference: payment.originalPaymentId || null,
		channel: payment.channel,
		netAmount: taxRows.map((row) => row.netAmount || 0).reduce((a, b) => a + b, 0),
		taxRows,
		taxAmount: taxRows.map((row) => row.taxAmount || 0).reduce((a, b) => a + b, 0),
		totalAmount: taxRows.map((row) => row.totalAmount).reduce((a, b) => a + b, 0),
		feeRows,
		...(itemNameRows && { itemNameRows }),
		...(shopHasCustomAccountingGroups && { customAccountingGroupRows }),
		paymentId: payment.id,
		orderId: payment.orderId,
		methodRows,
		receiptId: payment.receiptId || null,
		currency: payment.amount.currency,
		locationId: payment.locationId,
		transactionIds: transactions.map((t) => t.id),
	};
	return paymentRow;
};

export const sortAccountingRowsByName = (a: AccountingRow, b: AccountingRow) => {
	const nameA = a.name.toUpperCase();
	const nameB = b.name.toUpperCase();
	return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
};

export const sortAccountingRowsByUnits = (a: AccountingRow, b: AccountingRow) =>
	(b.units || 1) - (a.units || 1);

export const isAccountingGroupFilterApplied = (
	selectedAccountingGroupIds: string[],
	allAccountingGroups: AccountingGroup[],
) =>
	!!selectedAccountingGroupIds.length &&
	selectedAccountingGroupIds.length !== allAccountingGroups.length;
